import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useCoefficientsConfig } from './configuration';
import { useSeasonYear } from './year';

import type { FetchCoefficientsParams } from '@apis/coefficients';
import { fetchCoefficients } from '@apis/coefficients';
import { CoefficientRange, CoefficientType } from '@shared-types/coefficient.types';
import { CompetitionSex } from '@shared-types/competition.types';

export const useCoefficients = () => {
  const { configuration } = useCoefficientsConfig();
  const { seasonYear } = useSeasonYear();

  const params: FetchCoefficientsParams = useMemo(() => {
    let season = seasonYear ?? `${configuration.currentSeason}`;

    let coefficientRange: CoefficientRange = CoefficientRange[configuration.mode];
    let coefficientType: CoefficientType =
      CoefficientType[
        `${configuration.competitionSex === CompetitionSex.MALE ? 'MEN' : 'WOMEN'}_${configuration.competitionType}`
      ];
    if (configuration.mode === 'TENYEARS') {
      coefficientRange = CoefficientRange.OVERALL;
      coefficientType = CoefficientType.MEN_CLUB_TEN_YEARS;
    } else if (configuration.mode === 'FUTELO') {
      coefficientRange = CoefficientRange.OVERALL;
      coefficientType = CoefficientType.FUTELO;
      season = null;
    } else if (!configuration.enableCalendar) {
      season = `${configuration.currentSeason}`;
    }

    return {
      coefficientRange,
      coefficientType,
      seasonYear: season,
    };
  }, [configuration, seasonYear]);

  const {
    data: { members, lastUpdateDate },
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['coefficients', params],
    queryFn: ({ signal }) => fetchCoefficients(params, signal),
    placeholderData: { members: [], lastUpdateDate: null },
  });

  return { coefficients: members, lastUpdateDate: lastUpdateDate, isLoading: isLoading || isFetching };
};
